import { useState } from 'react';
import { toast } from 'sonner';
import { Play, Loader2, Square } from 'lucide-react';
import { simulate, cancelSim } from '../lib/aquaNet';
import { setAqua, useAqua } from '../lib/aquaStore';
import { AquaQuality } from './AquaQuality';
import { SourcePick } from './AquaSourcePick';

const n1 = (v, d = 1) => (v == null || !Number.isFinite(v) ? '–' : v.toFixed(d).replace('.', ','));

// water age / water exchange from the time-based calculation (warm-up, then the last 24 h are evaluated)
export function AquaAge({ t, project, updateProject, cfg, setCfg }) {
  const s = useAqua(), [busy, setBusy] = useState(false), [err, setErr] = useState(null);
  const E = cfg.eps || {}, hours = Math.max(48, Math.min(336, +E.hours || 72)), lim = +E.lim || 24;
  const set = (p) => setCfg({ eps: { ...E, ...p } });
  const byId = Object.fromEntries((project.fittings || []).map((f) => [f.id, f])), nm = (fid) => byId[fid]?.name || t['fit_' + byId[fid]?.type] || fid;
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await simulate(project, cfg, { eps: { hours, profile: E.profile || 'res' } });
      if (r.error) setErr(r); else setAqua({ eps: { ...r.eps, quality: r.quality, at: r.at }, on: true, mode: 'age' });
    } catch (e) { toast.error(String(e?.message || e)); }
    setBusy(false);
  };
  const e = s.eps, nodes = s.res?.nodes || {};
  const top = e ? Object.entries(e.age).filter(([id]) => !id.includes('~b')).sort((a, b) => b[1] - a[1]).slice(0, 8) : [];
  return (
    <>
      <p className="muted">{t.aqAgeHint}</p>
      <div className="aq-grid">
        <label>{t.aqAgeHours}<input type="number" min="48" max="336" step="24" data-testid="aq-age-hours" value={hours} onChange={(ev) => set({ hours: ev.target.value })} /> h</label>
        <label>{t.aqAgeProfile}<select data-testid="aq-age-profile" value={E.profile || 'res'} onChange={(ev) => set({ profile: ev.target.value })}><option value="res">{cfg.prof?.length === 24 ? t.dprofOwn : t.aqProf_res}</option><option value="flat">{t.aqProf_flat}</option></select></label>
        <label>{t.aqAgeLim}<input type="number" min="1" step="1" data-testid="aq-age-lim" value={lim} onChange={(ev) => set({ lim: ev.target.value })} /> h</label>
      </div>
      <p className="aq-disc">{t.aqAgeMethod.replace('{h}', hours).replace('{w}', hours - 24)}</p>
      <div className="aq-row">
        <button className="btn btn-accent aq-run" data-testid="aq-age-run" disabled={busy} onClick={run}>{busy ? <Loader2 size={16} className="spin" /> : <Play size={16} />} {t.aqAgeRun}</button>
        {busy && <button className="btn btn-outline" data-testid="aq-age-cancel" onClick={cancelSim}><Square size={14} /> {t.aqCancel}</button>}
      </div>
      {err?.error === 'noSource' && <SourcePick t={t} project={project} updateProject={updateProject} />}
      {err && err.error !== 'noSource' && <p className="aq-err" data-testid="aq-age-error">{t['aqErr_' + err.error] || t.aqErr_calc}{err.detail ? ` (${err.detail})` : ''}</p>}
      {e && (
        <div className="aq-res" data-testid="aq-age-result">
          <div className="aq-kpi"><b data-testid="aq-age-max">{n1(Math.max(...Object.values(e.age)))} h</b><small>{t.aqAgeMaxL}</small></div>
          {top.map(([id, a]) => (
            <div key={id} className={`aq-row ${a >= lim ? 'aq-w-warm' : ''}`} data-testid={`aq-age-node-${nodes[id]?.fid || id}`}>
              <span>{nodes[id]?.fid ? nm(nodes[id].fid) : nm(id)}</span><b>{n1(a)} h</b><small>max {n1(e.ageMax[id])} h</small>{a >= lim && <small>⚠ {t.aqAgeHigh}</small>}
            </div>
          ))}
          {e.tanks.map((k) => (
            <div key={k.id} className="aq-row" data-testid={`aq-tank-${k.fid}`}>
              <span>🛢 {nm(k.fid)}</span><small>{t.aqTankLvl}: {n1(k.min, 2)}–{n1(k.max, 2)} m ({t.aqTankLim} {n1(k.lim[0], 2)}–{n1(k.lim[1], 2)} m)</small><b>{Number.isFinite(k.turnoverH) ? `${n1(k.turnoverH / 24, 1)} d` : '∞'}</b><small>{t.aqTankTurn}</small>
            </div>
          ))}
          {top.some(([, a]) => a >= lim) && <p className="aq-warn" data-testid="aq-age-warn">{t.aqAgeWarn}</p>}
          <AquaQuality t={t} q={e.quality} />
          <p className="aq-disc">{t.aqAgeDisc}</p>
        </div>
      )}
    </>
  );
}
